import { useCallback, useState } from 'react';
import { useAppDispatch } from '@/store';
import { setEventRSVP } from '@/store/slices/residents/homeSlice';
import { useToast } from '@/contexts/admin/ToastContext';
import { Event } from '@/types/residents/event';

interface UseEventRsvpOptions {
  acceptSuccessMessage?: string;
  declineSuccessMessage?: string;
}

export function useEventRsvp(event: Pick<Event, 'id' | 'rsvp'>, options: UseEventRsvpOptions = {}) {
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const accept = useCallback(async () => {
    if (!event?.id) return;
    try {
      setIsUpdating(true);
      await dispatch(setEventRSVP({ eventId: event.id, rsvp: true })).unwrap();
      if (options.acceptSuccessMessage) {
        showSuccess(options.acceptSuccessMessage);
      }
    } catch (error: any) {
      const message = (error && error.message) || 'Failed to update RSVP';
      showError(message);
    } finally {
      setIsUpdating(false);
    }
  }, [dispatch, event?.id, options.acceptSuccessMessage, showError, showSuccess]);

  const decline = useCallback(async () => {
    if (!event?.id) return;
    try {
      setIsUpdating(true);
      await dispatch(setEventRSVP({ eventId: event.id, rsvp: false })).unwrap();
      if (options.declineSuccessMessage) {
        showSuccess(options.declineSuccessMessage);
      }
    } catch (error: any) {
      const message = (error && error.message) || 'Failed to update RSVP';
      showError(message);
    } finally {
      setIsUpdating(false);
    }
  }, [dispatch, event?.id, options.declineSuccessMessage, showError, showSuccess]);

  const toggle = useCallback(async () => {
    if (!event?.id) return;
    const next = !event.rsvp;
    if (next) {
      await accept();
    } else {
      await decline();
    }
  }, [accept, decline, event?.id, event?.rsvp]);

  return { accept, decline, toggle, isUpdating };
}


